import { Link } from "react-router-dom";
import { ArrowRight, Certificate, Users, Star } from "@phosphor-icons/react";

const About = () => {
  const teamMembers = [
    {
      name: "Dr. Elena Chambers",
      title: "Founder & Medical Director",
      credentials: "MD, FACS",
      image: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
    },
    {
      name: "Dr. Marcus Wei",
      title: "Lead Aesthetic Physician",
      credentials: "MD, Board Certified",
      image: "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
    },
    {
      name: "Sofia Rodriguez",
      title: "Advanced Practice Nurse",
      credentials: "NP, Aesthetic Specialist",
      image: "https://images.unsplash.com/photo-1594824476967-48c8b964273f?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
    },
  ];

  const values = [
    {
      icon: <Certificate size={32} weight="light" />,
      title: "Medical Excellence",
      description: "Board-certified physicians with advanced training in aesthetic medicine and facial anatomy.",
    },
    {
      icon: <Star size={32} weight="light" />,
      title: "Artistic Vision",
      description: "We approach each face as a unique canvas, honoring natural beauty while enhancing features.",
    },
    {
      icon: <Users size={32} weight="light" />,
      title: "Personal Care",
      description: "Every treatment plan is customized to your individual goals, lifestyle, and aesthetic preferences.",
    },
  ];

  return (
    <div data-testid="about-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-24 md:py-32 lg:py-40 bg-cream">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-taupe mb-4">
                Our Story
              </p>
              <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[0.9] text-matte">
                Where Art
                <br />
                Meets Science
              </h1>
              <p className="mt-8 text-base md:text-lg font-light text-matte/70 leading-relaxed">
                Medical Aesthetic Arts was founded on the belief that true beauty 
                enhancement requires both scientific precision and artistic vision. 
                Our approach combines the latest medical advancements with a deep 
                understanding of facial aesthetics and proportion.
              </p>
            </div>
            <div className="image-reveal aspect-[4/5] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1691934338633-178293da4a99?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Medical aesthetic tools artistically arranged"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-24 md:py-32 bg-smoke">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-xs uppercase tracking-[0.3em] text-taupe mb-4">
              Our Philosophy
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-matte">
              The Art of Facial Design
            </h2>
            <p className="mt-8 text-base font-light text-matte/70 leading-relaxed">
              We believe that the most beautiful results are those that appear entirely natural. 
              Our facial design philosophy centers on understanding the unique geometry of each 
              face and creating harmony through subtle, precise enhancements. We never aim to 
              change who you are we aim to reveal the best version of you.
            </p>
          </div>

          <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div
                key={value.title}
                data-testid={`value-card-${index}`}
                className="text-center p-8"
              >
                <div className="inline-flex text-taupe mb-6">
                  {value.icon}
                </div>
                <h3 className="font-serif text-2xl font-light text-matte mb-4">
                  {value.title}
                </h3>
                <p className="text-sm font-light text-matte/60 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 md:py-32 lg:py-40 bg-cream">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center mb-16 md:mb-24">
            <p className="text-xs uppercase tracking-[0.3em] text-taupe mb-4">
              Our Team
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-matte">
              Meet The Artists
            </h2>
            <p className="mt-6 text-base font-light text-matte/60 max-w-lg mx-auto">
              Our team of skilled practitioners brings together decades of medical 
              expertise and artistic training.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {teamMembers.map((member, index) => (
              <div
                key={member.name}
                data-testid={`team-member-${index}`}
                className="group"
              >
                <div className="image-reveal aspect-[3/4] overflow-hidden mb-6">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="font-serif text-2xl font-light text-matte">
                  {member.name}
                </h3>
                <p className="text-sm text-taupe mt-1">{member.credentials}</p>
                <p className="text-sm font-light text-matte/60 mt-2">
                  {member.title}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Credentials Section */}
      <section className="py-24 md:py-32 bg-smoke">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="image-reveal aspect-square overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1677568554453-ae5baf28da6c?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Luxury clinic interior"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-taupe mb-4">
                Our Commitment
              </p>
              <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-matte">
                Excellence in Every Detail
              </h2>
              <div className="mt-8 space-y-6">
                <div className="border-l-2 border-taupe pl-6">
                  <h4 className="font-serif text-xl text-matte mb-2">
                    Advanced Training
                  </h4>
                  <p className="text-sm font-light text-matte/60 leading-relaxed">
                    Our physicians maintain the highest certifications and continuously 
                    advance their skills through ongoing education.
                  </p>
                </div>
                <div className="border-l-2 border-taupe pl-6">
                  <h4 className="font-serif text-xl text-matte mb-2">
                    Premium Products
                  </h4>
                  <p className="text-sm font-light text-matte/60 leading-relaxed">
                    We use only FDA-approved, medical-grade products from the most 
                    trusted names in aesthetic medicine.
                  </p>
                </div>
                <div className="border-l-2 border-taupe pl-6">
                  <h4 className="font-serif text-xl text-matte mb-2">
                    Luxurious Environment
                  </h4>
                  <p className="text-sm font-light text-matte/60 leading-relaxed">
                    Our clinic is designed to provide a serene, spa-like atmosphere 
                    where you can relax and feel pampered.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-cream">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-matte">
            Ready to Begin?
          </h2>
          <p className="mt-6 text-base font-light text-matte/60 max-w-lg mx-auto">
            Schedule a consultation to discuss your aesthetic goals with our expert team.
          </p>
          <Link
            to="/contact"
            data-testid="about-cta-btn"
            className="btn-primary inline-flex items-center gap-3 mt-10"
          >
            Book Consultation
            <ArrowRight size={16} weight="light" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
